<template>
  <v-row>
    <v-col>
      <v-row dense>
        <v-col class="text-h5 gray--text">
          Anestesia
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="6">
          <v-combobox
            v-model="anesthetist"
            label="Anestesista"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            :disabled="disabled"
            @change="handleChange"
          />
        </v-col>

        <v-col cols="6">
          <v-combobox
            v-model="assistants"
            label="Auxiliares"
            dense
            hide-details
            outlined
            :items="nurseUsers"
            item-text="employee.name"
            multiple
            chips
            deletable-chips
            small-chips
            background-color="white"
            :disabled="disabled"
            @change="handleChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-combobox
            v-model="descriptionModel"
            label="Anestesista"
            dense
            hide-details
            outlined
            :items="descriptionModels"
            item-text="name"
            background-color="white"
            :disabled="disabled"
          />
        </v-col>

        <v-col>
          <v-combobox
            v-model="descriptionTemplate"
            label="Modelo de anestesista"
            dense
            hide-details
            outlined
            :items="descriptionModelTemplates"
            item-text="name"
            background-color="white"
            :disabled="disabled"
            @change="choseDescriptionTemplate"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <RichText
            v-model="description"
            :disabled="disabled"
            @change="handleChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="text-h5 gray--text">
          Anexos Anestesia
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <FileUpload
            ownerType="Surgery::Intraoperative::Anesthesia"
            :ownerId="anesthesia.id"
            :disabled="disabled"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "SurgeryIntraoperativeAnesthesia",

  props: {
    attendance: Object,
    surgery: Object,
    refreshSurgery: Function,
    doctorUsers: Array,
    nurseUsers: Array,

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      initializing: true,
      hasUnsavedChanges: false,
      initialSnapshot: null,

      anesthetist: null,
      assistants: [],
      description: "",

      descriptionModel: null,
      descriptionTemplate: null,
    };
  },

  computed: {
    anesthesia() {
      return this.surgery.intraoperative.anesthesia;
    },

    /*
     * Observamos os IDs em vez da referência dos arrays.
     *
     * O componente pai pode recalcular doctorUsers e nurseUsers e entregar
     * um novo array mesmo quando os usuários continuam os mesmos.
     */
    doctorUsersSignature() {
      return (this.doctorUsers || [])
        .map((user) => String(user?.id || ""))
        .filter(Boolean)
        .sort()
        .join("|");
    },

    nurseUsersSignature() {
      return (this.nurseUsers || [])
        .map((user) => String(user?.id || ""))
        .filter(Boolean)
        .sort()
        .join("|");
    },

    descriptionModels() {
      return this.lodash.uniqBy(
        this.lodash.map(
          this.surgeryIntraoperativeAnesthesiaTypeTemplates || [],
          "anesthesiaType"
        ),
        "id"
      );
    },

    descriptionModelTemplates() {
      if (!this.descriptionModel?.id) {
        return [];
      }

      return this.lodash.filter(
        this.surgeryIntraoperativeAnesthesiaTypeTemplates || [],
        (template) =>
          template.anesthesiaType.id === this.descriptionModel.id
      );
    },
  },

  watch: {
    doctorUsersSignature() {
      this.hydrateUsers();
    },

    nurseUsersSignature() {
      this.hydrateUsers();
    },
  },

  async mounted() {
    this.initializing = true;

    this.description = this.anesthesia.description || "";

    await this.hydrateUsers();
    await this.$nextTick();

    /*
     * O estado integralmente carregado passa a ser o baseline.
     */
    this.captureSnapshot();

    this.initializing = false;
  },

  apollo: {
    surgeryIntraoperativeAnesthesiaTypeTemplates: {
      query: require(
        "@/graphql/surgery/surgery_intraoperative_anesthesia_type_templates.gql"
      ),
    },
  },

  methods: {
    normalizeId(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return null;
      }

      return String(value);
    },

    /*
     * Normaliza diferenças comuns geradas pelo editor RichText.
     *
     * Exemplos:
     *   ""              e "<p><br></p>"
     *   "Texto"         e "<p>Texto</p>"
     *   "&nbsp;"        e espaço normal
     */
    normalizeRichText(value) {
      let normalized = String(value || "")
        .replace(/\u00a0/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(
          /<(p|div)>\s*(<br\s*\/?>)?\s*<\/\1>/gi,
          ""
        )
        .replace(/>\s+</g, "><")
        .trim();

      /*
       * Considera equivalente um texto simples e o mesmo texto
       * encapsulado em um único parágrafo pelo editor.
       */
      if (
        /^<p>[\s\S]*<\/p>$/i.test(normalized) &&
        !/<\/p>\s*<p>/i.test(normalized)
      ) {
        normalized = normalized
          .replace(/^<p>/i, "")
          .replace(/<\/p>$/i, "")
          .trim();
      }

      if (
        /^<div>[\s\S]*<\/div>$/i.test(normalized) &&
        !/<\/div>\s*<div>/i.test(normalized)
      ) {
        normalized = normalized
          .replace(/^<div>/i, "")
          .replace(/<\/div>$/i, "")
          .trim();
      }

      return normalized;
    },

    createSnapshot() {
      return JSON.stringify({
        anesthetistId: this.normalizeId(
          this.lodash.get(
            this.anesthetist,
            "id",
            null
          )
        ),

        assistantIds: this.lodash
          .map(this.assistants || [], "id")
          .map(this.normalizeId)
          .filter(Boolean)
          .sort(),

        description: this.normalizeRichText(
          this.description
        ),
      });
    },

    captureSnapshot() {
      this.initialSnapshot = this.createSnapshot();
      this.hasUnsavedChanges = false;
    },

    updateDirtyState() {
      if (
        this.initializing ||
        this.initialSnapshot === null
      ) {
        return;
      }

      const isDirty =
        this.createSnapshot() !== this.initialSnapshot;

      this.hasUnsavedChanges = isDirty;

      /*
       * O pai passa a receber o estado atual:
       * true quando diferente do snapshot e false quando voltou ao original.
       */
      this.$emit("dirty-change", isDirty);
    },

    handleChange() {
      if (this.initializing || this.disabled) {
        return;
      }

      /*
       * Aguarda o v-model receber o valor definitivo antes da comparação.
       */
      this.$nextTick(() => {
        if (this.initializing || this.disabled) {
          return;
        }

        this.updateDirtyState();
      });
    },

    loadUsers() {
      if (this.anesthesia.anesthetist?.id) {
        this.anesthetist = this.lodash.find(
          this.doctorUsers || [],
          {
            id: this.anesthesia.anesthetist.id,
          }
        );
      } else {
        this.anesthetist = null;
      }

      const assistantIds = this.lodash
        .map(
          this.anesthesia.assistants || [],
          "id"
        )
        .map(this.normalizeId)
        .filter(Boolean);

      this.assistants = this.lodash.filter(
        this.nurseUsers || [],
        (user) =>
          assistantIds.includes(
            this.normalizeId(user.id)
          )
      );
    },

    async hydrateUsers() {
      /*
       * Nunca sobrescreve uma edição que o operador já iniciou.
       */
      if (this.hasUnsavedChanges) {
        return;
      }

      const previousInitializingState =
        this.initializing;

      this.initializing = true;

      try {
        this.loadUsers();

        await this.$nextTick();

        /*
         * A chegada assíncrona das listas é hidratação, não edição.
         */
        this.captureSnapshot();
      } finally {
        this.initializing =
          previousInitializingState;
      }
    },

    async save() {
      if (
        this.disabled ||
        !this.hasUnsavedChanges
      ) {
        return;
      }

      /*
       * Guarda exatamente o estado enviado ao backend.
       *
       * Caso o operador altere algo durante a mutation, o estado
       * posterior continuará sendo reconhecido como não salvo.
       */
      const snapshotBeingSaved =
        this.createSnapshot();

      await this.apolloMutate({
        mutation: require(
          "@/graphql/surgery/save_surgery_intraoperative_anesthesia.gql"
        ),

        variables: {
          surgeryId: this.surgery.id,

          anesthetistId: this.lodash.get(
            this.anesthetist,
            "id",
            ""
          ),

          description: this.description,

          assistantIds: this.lodash.map(
            this.assistants || [],
            "id"
          ),
        },
      });

      this.descriptionModel = null;
      this.descriptionTemplate = null;

      /*
       * O estado enviado passa a ser o novo baseline.
       */
      this.initialSnapshot = snapshotBeingSaved;

      /*
       * Recalcula porque o operador pode ter editado durante a request.
       * Também informa false ao pai quando tudo foi salvo.
       */
      this.updateDirtyState();
    },

    choseDescriptionTemplate() {
      if (!this.descriptionTemplate?.description) {
        return;
      }

      if (!this.description) {
        this.description =
          this.descriptionTemplate.description;
      } else {
        this.description += "\n";
        this.description +=
          this.descriptionTemplate.description;
      }

      this.handleChange();
    },
  },
};
</script>

<style lang="scss" scoped></style>
